import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, axios } from '../../context/AuthContext';

export default function OgretmenDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ogrenciler, setOgrenciler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [dersler, setDersler] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get('/ogrenciler?limit=50'),
      axios.get('/siniflar'),
      axios.get('/dersler'),
    ]).then(([o, s, d]) => {
      if (o.data.success) setOgrenciler(o.data.data);
      if (s.data.success) setSiniflar(s.data.data);
      if (d.data.success) setDersler(d.data.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="page-loader">
      <div className="spinner" style={{ width: 36, height: 36 }} />
    </div>
  );

  const QUICK_LINKS = [
    { label: 'Ödevler',         icon: '▦', path: '/ogretmen/odevler',  color: '#5B8DD4' },
    { label: 'Ders Programım',  icon: '▧', path: '/ogretmen/program', color: '#E05A47' },
    { label: 'Denemeler',       icon: '▨', path: '/ogretmen/denemeler', color: '#27AE60' },
    { label: 'Müfredat Takibi', icon: '📖', path: '/ogretmen/mufredat', color: '#D4853A' },
    { label: 'Randevular',      icon: '▤', path: '/ogretmen/randevular', color: '#9B72CF' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* Welcome & Hızlı Yoklama Butonu */}
      <div className="welcome-banner animate-fadeInDown" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '28px 32px',
        background: 'linear-gradient(135deg, var(--accent) 0%, #1A365D 100%)',
        borderRadius: 'var(--r-xl)',
        color: 'white',
        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
      }}>
        <div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 6 }}>Öğretmen Paneli</div>
          <h1 style={{ fontSize: '1.8rem', marginBottom: 4, fontFamily: 'var(--font-ui)', fontWeight: 800 }}>Merhaba, {user?.ad}</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem' }}>
            Bugün harika bir gün! Sınıfınıza gidip dersinize başlayabilirsiniz.
          </p>
        </div>
        
        {/* Hızlı Yoklama Kısayolu */}
        <button 
          onClick={() => navigate('/ogretmen/yoklama')}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            background: 'white', 
            color: 'var(--accent)', 
            border: 'none',
            padding: '16px 24px',
            borderRadius: 'var(--r-lg)',
            cursor: 'pointer',
            fontWeight: 800,
            fontSize: '1rem',
            fontFamily: 'var(--font-ui)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <span style={{ fontSize: '1.4rem' }}>📝</span>
          Hemen Yoklama Al
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-3 stagger">
        {[
          { label: 'Öğrencileriniz', value: ogrenciler.length, icon: '👨‍🎓', color: '#3AACCF' },
          { label: 'Sınıflarınız',   value: siniflar.length,   icon: '🏫', color: '#5B8DD4' },
          { label: 'Dersleriniz',    value: dersler.length,    icon: '📚', color: '#27AE60' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ '--stat-color': s.color, background: 'var(--bg-card)', padding: '24px', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: '2.5rem' }}>{s.icon}</div>
            <div>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.8rem', color: 'var(--text-primary)', lineHeight: 1.2 }}>{s.value}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div className="grid stagger" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {QUICK_LINKS.map((q, i) => (
          <button
            key={i}
            onClick={() => navigate(q.path)}
            style={{
              background: `${q.color}10`,
              border: `1px solid ${q.color}30`,
              borderRadius: 'var(--r-lg)',
              padding: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 12,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = `${q.color}18`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = `${q.color}10`; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ width: 40, height: 40, borderRadius: 'var(--r-md)', background: `${q.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: q.color }}>
              {q.icon}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: 2 }}>{q.label}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Git →</div>
            </div>
          </button>
        ))}
      </div>

    </div>
  );
}
